import { Brain, Users, Activity, FileText, Book, Microscope, HeartPulse, Stethoscope } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Hero } from '../../components/Hero';
import { AnimatedSection } from '../../components/AnimatedSection';

export function Neurodevelopmental() {
  return (
    <div>
      <Hero
        title="Neurodevelopmental Disorders"
        description="Expert evaluation and comprehensive treatment of conditions affecting neurological development, providing specialized care and support for both children and adults with developmental challenges."
        image="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&q=80"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <AnimatedSection>
          <div className="grid md:grid-cols-2 gap-12 mb-16">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Comprehensive Developmental Care</h2>
              <p className="text-gray-600 mb-6">
                Our clinic provides expert diagnosis and treatment for a wide range of neurodevelopmental 
                disorders, utilizing advanced diagnostic tools and evidence-based treatment approaches. 
                We understand that each individual's presentation is unique, requiring personalized 
                care strategies that address cognitive, behavioral, social, and emotional aspects of 
                development.
              </p>
              <p className="text-gray-600">
                Our integrated approach combines neurological expertise with specialized therapeutic 
                interventions, working closely with families, schools, and other healthcare providers 
                to ensure comprehensive care and optimal outcomes for each patient.
              </p>
            </div>
            <div className="bg-gradient-cool rounded-lg p-8 shadow-glow">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Conditions We Treat</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <Brain className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">ADHD</h4>
                    <p className="text-gray-600">Comprehensive evaluation and management of attention-deficit/hyperactivity disorder across the lifespan</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <Users className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Autism Spectrum Disorders</h4>
                    <p className="text-gray-600">Specialized assessment and intervention for social communication and behavioral challenges</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <Activity className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Tourette's Syndrome</h4>
                    <p className="text-gray-600">Expert management of tics and associated conditions using CBIT and other evidence-based approaches</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <Book className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Learning Disorders</h4>
                    <p className="text-gray-600">Evaluation and support for dyslexia, dyscalculia, and other specific learning challenges</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </AnimatedSection>

        <AnimatedSection delay={0.2}>
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Our Diagnostic Approach</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
                <div className="mb-6">
                  <img
                    src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&q=80"
                    alt="Comprehensive Assessment"
                    className="w-full h-40 object-cover rounded-lg mb-4"
                  />
                </div>
                <Microscope className="h-8 w-8 text-blue-600 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Comprehensive Assessment</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Detailed developmental history</li>
                  <li>• Cognitive function evaluation</li>
                  <li>• Behavioral assessment</li>
                  <li>• Social-emotional screening</li>
                  <li>• Academic performance review</li>
                  <li>• Sensory processing evaluation</li>
                </ul>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
                <div className="mb-6">
                  <img
                    src="https://images.unsplash.com/photo-1532094349884-543bc11b234d?auto=format&fit=crop&q=80"
                    alt="Specialized Testing"
                    className="w-full h-40 object-cover rounded-lg mb-4"
                  />
                </div>
                <Brain className="h-8 w-8 text-blue-600 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Specialized Testing</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Neuropsychological testing</li>
                  <li>• ADHD screening tools</li>
                  <li>• Autism diagnostic protocols</li>
                  <li>• Learning disability assessments</li>
                  <li>• Executive function testing</li>
                  <li>• Motor skills evaluation</li>
                </ul>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
                <div className="mb-6">
                  <img
                    src="https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?auto=format&fit=crop&q=80"
                    alt="Treatment Planning"
                    className="w-full h-40 object-cover rounded-lg mb-4"
                  />
                </div>
                <HeartPulse className="h-8 w-8 text-blue-600 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Collaborative Care</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Family involvement</li>
                  <li>• School coordination</li>
                  <li>• Therapy team integration</li>
                  <li>• Progress monitoring</li>
                  <li>• Treatment plan updates</li>
                  <li>• Support system engagement</li>
                </ul>
              </div>
            </div>
          </div>
        </AnimatedSection>

        <AnimatedSection delay={0.4}>
          <div className="bg-gradient-healing rounded-lg p-8 mb-16 shadow-glow">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Treatment Services</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Behavioral Interventions</h3>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <Users className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                    <span>Comprehensive Behavioral Intervention for Tics (CBIT)</span>
                  </li>
                  <li className="flex items-start">
                    <Brain className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                    <span>Cognitive Behavioral Therapy (CBT)</span>
                  </li>
                  <li className="flex items-start">
                    <Activity className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                    <span>Social skills training programs</span>
                  </li>
                  <li className="flex items-start">
                    <Stethoscope className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                    <span>Parent training and support</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Medical Management</h3>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <FileText className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                    <span>Medication evaluation and monitoring</span>
                  </li>
                  <li className="flex items-start">
                    <Brain className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                    <span>Treatment of co-occurring conditions</span>
                  </li>
                  <li className="flex items-start">
                    <HeartPulse className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                    <span>Regular progress assessments</span>
                  </li>
                  <li className="flex items-start">
                    <Activity className="h-5 w-5 text-blue-600 mr-2 mt-1" />
                    <span>Coordination with other providers</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </AnimatedSection>

        <AnimatedSection delay={0.6}>
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Support Services</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
                <div className="mb-6">
                  <img
                    src="https://images.unsplash.com/photo-1551601651-2a8555f1a136?auto=format&fit=crop&q=80"
                    alt="Educational Support"
                    className="w-full h-40 object-cover rounded-lg"
                  />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Educational Support</h3>
                <p className="text-gray-600">
                  Collaboration with schools to implement effective learning strategies, develop 
                  IEP/504 plans, and ensure appropriate academic accommodations. Includes regular 
                  communication with teachers and school staff.
                </p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
                <div className="mb-6">
                  <img
                    src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&q=80"
                    alt="Family Resources"
                    className="w-full h-40 object-cover rounded-lg"
                  />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Family Resources</h3>
                <p className="text-gray-600">
                  Comprehensive support for families including parent education, support groups, 
                  resource coordination, and guidance on accessing community services and advocacy 
                  organizations.
                </p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-glow transition-shadow">
                <div className="mb-6">
                  <img
                    src="https://images.unsplash.com/photo-1581056771107-24ca5f033842?auto=format&fit=crop&q=80"
                    alt="Transition Planning"
                    className="w-full h-40 object-cover rounded-lg"
                  />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Transition Planning</h3>
                <p className="text-gray-600">
                  Support for life transitions, including school changes, entering adulthood, 
                  and career planning. Focus on developing independence and self-advocacy skills.
                </p>
              </div>
            </div>
          </div>
        </AnimatedSection>

        <AnimatedSection delay={0.8}>
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Start Your Journey</h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Early intervention and appropriate support can make a significant difference in 
              developmental outcomes. Contact us to learn how our comprehensive care approach 
              can help your child or loved one reach their full potential.
            </p>
            <div className="flex justify-center gap-4">
              <Link
                to="/contact"
                className="bg-blue-600 text-white px-8 py-3 rounded-md font-semibold hover:bg-blue-700 transition-colors"
              >
                Schedule an Evaluation
              </Link>
              <Link
                to="/resources/education"
                className="bg-white text-blue-600 border border-blue-600 px-8 py-3 rounded-md font-semibold hover:bg-blue-50 transition-colors"
              >
                Learn More
              </Link>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </div>
  );
}